import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { gsap } from 'gsap';
import MotionPathPlugin from 'gsap/MotionPathPlugin';
import { motion } from 'motion/react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavigateOptions, To, useLocation } from 'react-router-dom';
import { useTheme } from 'styled-components';

import { dashboardUserListInvitesOptions, dashboardUserListInvitesQueryKey } from '@column/api';
import { Button, Chip } from '@column/column-ui-kit';

import { ROUTE as APP_ROUTE } from '~/app/routes';
import { Loading } from '~/components';
import { Logo } from '~/elements/Logo';
import { sandboxOptions } from '~/lib/api/sandboxOptions';
import { useNavigate } from '~/lib/navigation';
import { animateLogo } from '~/public/pages/_components/Logo';
import { ROUTE as PUBLIC_ROUTE } from '~/public/routes';
import { getRoleLabel } from '~/repositories/RoleRepository';
import { UserRepository } from '~/repositories/UserRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';

interface PlatformInvitePageProps {
  platformId: string;
}

const INVITE_PANEL_OPACITY_DURATION = 0.12;

interface PendingNavigation {
  animateLogo?: boolean;
  clearInviteQuery?: boolean;
  options?: NavigateOptions;
  to: To;
}

interface PlatformInviteLayoutProps extends React.PropsWithChildren {
  isExiting: boolean;
  isTransitioning: boolean;
  logoRef: React.RefObject<SVGSVGElement | null>;
  logoWrapperRef: React.RefObject<HTMLDivElement | null>;
  onExitComplete: () => void;
  panelRef: React.RefObject<HTMLDivElement | null>;
}

const PlatformInviteLayout: React.FC<PlatformInviteLayoutProps> = ({
  children,
  isExiting,
  isTransitioning,
  logoRef,
  logoWrapperRef,
  onExitComplete,
  panelRef,
}) => {
  const theme = useTheme();

  return (
    <main className="box-border h-[100svh] overflow-y-auto text-foreground" style={{ backgroundColor: theme.body }}>
      <div className="relative z-[1] flex min-h-[100svh] flex-col justify-stretch py-[clamp(92px,14vh,164px)] max-[600px]:py-[clamp(92px,10vh,112px)]">
        <div className="relative grid grow">
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: isExiting ? 0 : 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
            onAnimationComplete={() => {
              if (isExiting) {
                onExitComplete();
              }
            }}
            className={`relative mx-auto grid w-full max-w-[320px] self-center box-border gap-8 px-5 text-center ${
              isTransitioning ? 'pointer-events-none' : ''
            }`}
          >
            <div ref={logoWrapperRef} className="absolute top-0 left-1/2">
              <Logo
                ref={logoRef}
                className="-ml-3 origin-top-left overflow-visible [&>path]:[opacity:var(--logo-path-opacity,0)] [&>path]:[transform:translate(var(--logo-path-x,16px),var(--logo-path-y,24px))_translateZ(0)]"
                variant="full"
                size="24px"
              />
            </div>
            <div ref={panelRef} className="grid gap-8">
              <Logo className="mx-auto opacity-0" size="24px" />
              {children}
            </div>
          </motion.div>
        </div>
      </div>
    </main>
  );
};

export const PagePlatformInvite: React.FC<PlatformInvitePageProps> = ({ platformId }) => {
  const currentUser = useSessionStore((state) => state.currentUser);
  const loadSession = useSessionStore((state) => state.loadSession);
  const addDangerNotification = useNotificationStore((state) => state.addDangerNotification);
  const addSuccessNotification = useNotificationStore((state) => state.addSuccessNotification);
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const hasNavigatedRef = useRef(false);
  const hasStartedLogoAnimationRef = useRef(false);
  const logoRef = useRef<SVGSVGElement>(null);
  const logoWrapperRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [pendingNavigation, setPendingNavigation] = useState<PendingNavigation>();

  const navigateWithTransition = useCallback(
    (to: To, options?: NavigateOptions, clearInviteQuery?: boolean, shouldAnimateLogo?: boolean) => {
      setPendingNavigation({ animateLogo: shouldAnimateLogo, clearInviteQuery, options, to });
    },
    []
  );

  const navigateAfterInvite = useCallback(
    (options?: NavigateOptions, clearInviteQuery?: boolean, animateAcceptedInvite?: boolean) => {
      const hasVerifiedFactor = useSessionStore.getState().currentUser?.factors.some((factor) => factor.factorVerified);
      const destination = hasVerifiedFactor ? APP_ROUTE.ROOT : PUBLIC_ROUTE.SETUP_MFA;

      navigateWithTransition(destination, options, clearInviteQuery, hasVerifiedFactor && animateAcceptedInvite);
    },
    [navigateWithTransition]
  );

  const handleExitComplete = useCallback(() => {
    if (!pendingNavigation || hasNavigatedRef.current) {
      return;
    }

    hasNavigatedRef.current = true;
    void navigate(pendingNavigation.to, pendingNavigation.options);

    if (pendingNavigation.clearInviteQuery) {
      queryClient.removeQueries({
        queryKey: dashboardUserListInvitesQueryKey({ path: { dashboard_user_id: currentUser?.id ?? '' } }),
      });
    }
  }, [currentUser?.id, navigate, pendingNavigation, queryClient]);

  useEffect(() => {
    if (!pendingNavigation?.animateLogo || hasStartedLogoAnimationRef.current) {
      return;
    }

    hasStartedLogoAnimationRef.current = true;

    if (!logoRef.current || !logoWrapperRef.current || !panelRef.current) {
      handleExitComplete();
      return;
    }

    animateLogo(
      logoRef.current,
      logoWrapperRef.current,
      panelRef.current,
      gsap,
      MotionPathPlugin,
      handleExitComplete,
      'app',
      INVITE_PANEL_OPACITY_DURATION
    );
  }, [handleExitComplete, pendingNavigation?.animateLogo]);

  useEffect(() => {
    if (!new URLSearchParams(location.search).has('code')) {
      return;
    }

    navigate(
      {
        pathname: location.pathname,
        search: new URLSearchParams({ invite: platformId }).toString(),
      },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate, platformId]);

  const invitesQuery = useQuery({
    ...dashboardUserListInvitesOptions({ path: { dashboard_user_id: currentUser?.id ?? '' }, ...sandboxOptions }),
    enabled: !!currentUser?.id,
  });

  const invite = useMemo(
    () => invitesQuery.data?.invites?.find((entry) => entry.platform_id === platformId),
    [invitesQuery.data?.invites, platformId]
  );

  useEffect(() => {
    if (invitesQuery.isError) {
      addDangerNotification({ content: 'We couldn’t load your invitations. Please try again.' });
    }
  }, [addDangerNotification, invitesQuery.error, invitesQuery.isError]);

  const leaveInvite = () => {
    navigateAfterInvite({ replace: true });
  };

  const acceptInviteMutation = useMutation({
    mutationFn: async () => {
      await UserRepository.acceptInvite({ platformId });
      await loadSession({ preferredPlatformId: platformId, showGlobalLoading: false });
    },
    onSuccess: () => {
      addSuccessNotification({ content: 'Invite accepted' });

      navigateAfterInvite({ replace: true, state: { from: 'auth' } }, true, true);
    },
    onError: (error) => {
      addDangerNotification({
        content: error instanceof Error ? error.message : 'Something went wrong, please try again.',
      });
    },
  });

  return (
    <PlatformInviteLayout
      isExiting={!!pendingNavigation && !pendingNavigation.animateLogo}
      isTransitioning={!!pendingNavigation}
      logoRef={logoRef}
      logoWrapperRef={logoWrapperRef}
      onExitComplete={handleExitComplete}
      panelRef={panelRef}
    >
      <Loading centerSpinner isLoading={invitesQuery.isPending}>
        {!invite ? (
          <div className="flex flex-col items-center gap-6">
            <div className="flex flex-col items-center gap-2">
              <h1 className="w-full text-center text-base leading-5 font-medium text-foreground">
                Invitation unavailable
              </h1>
              <p className="text-sm leading-5 text-muted-foreground">
                This invitation may have expired, been withdrawn, or already been accepted.
              </p>
            </div>
            <div className="grid w-full gap-2">
              {invitesQuery.isError && (
                <Button fullWidth variant="secondary" onClick={() => invitesQuery.refetch()}>
                  Try again
                </Button>
              )}
              <Button fullWidth onClick={leaveInvite}>
                Continue to dashboard
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-6">
            <div className="flex flex-col items-center gap-2">
              <h1 className="w-full text-center text-base leading-5 font-medium text-foreground">
                Join {invite.platform_name}?
              </h1>
              <div className="flex items-center justify-center gap-1.5">
                <p className="text-sm leading-5 text-secondary-foreground">Your role will be set as</p>
                <Chip>{getRoleLabel(invite.platform_role)}</Chip>
              </div>
            </div>
            <div className="w-full">
              <Button
                fullWidth
                onClick={() => acceptInviteMutation.mutate()}
                isLoading={acceptInviteMutation.isPending}
                isDisabled={acceptInviteMutation.isPending}
              >
                Accept invitation
              </Button>
              <button
                type="button"
                className="mt-6 cursor-pointer border-0 bg-transparent p-0 text-xs leading-[1.5] font-medium text-muted-foreground transition-colors hover:text-secondary-foreground disabled:cursor-default disabled:opacity-50"
                onClick={leaveInvite}
                disabled={acceptInviteMutation.isPending}
              >
                Skip to dashboard
              </button>
            </div>
          </div>
        )}
      </Loading>
    </PlatformInviteLayout>
  );
};
